import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Calendar, Navigation, ExternalLink, Hotel, Bus, ChevronRight } from "lucide-react";

const VENUE_IMAGE = "https://images.unsplash.com/photo-1662879046665-b286adb83aad?w=800&q=80&auto=format";
const WAZE_URL = "https://waze.com/ul?q=Levona%20Arava&navigate=yes";
const CALENDAR_URL = "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Mariage+Netanael+%26+Agathe&dates=20261007/20261008&location=Levona%2C+Arava+Desert%2C+Israel";

const accommodations = [
  { nameKey: "accommodation_1_name", descKey: "accommodation_1_desc", distKey: "accommodation_1_distance", bookKey: "accommodation_1_book", waze: "https://waze.com/ul?q=Nof%20Zuqim&navigate=yes", bookUrl: "https://nofzuqim.co.il/en/orders-en/" },
  { nameKey: "accommodation_2_name", descKey: "accommodation_2_desc", distKey: "accommodation_2_distance", bookKey: "accommodation_2_book", waze: "https://waze.com/ul?q=Arava%20Land%20Tzukim&navigate=yes", bookUrl: "https://www.eretzarava.co.il/" },
  { nameKey: "accommodation_3_name", descKey: "accommodation_3_desc", distKey: "accommodation_3_distance", bookKey: "accommodation_3_book", waze: "https://waze.com/ul?q=Zman%20Arava%20Tzukim&navigate=yes", bookUrl: "https://zmanarava.co.il/" },
  { nameKey: "accommodation_4_name", descKey: "accommodation_4_desc", distKey: "accommodation_4_distance", waze: "https://waze.com/ul?q=Eilat&navigate=yes" },
];

const VenueSection = () => {
  const { t } = useTranslation();

  const fadeIn = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <section data-testid="venue-section" className="bg-cream py-24 md:py-32 px-6">
      <motion.div
        className="max-w-2xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {/* Section header */}
        <motion.div variants={fadeIn} className="text-center mb-12">
          <h2 className="font-serif text-charcoal text-3xl md:text-5xl tracking-tight mb-3">
            {t("venue_title")}
          </h2>
          <p className="font-sans text-secondary text-sm md:text-base tracking-wide">
            {t("venue_subtitle")}
          </p>
        </motion.div>

        {/* Venue Card */}
        <motion.div
          variants={fadeIn}
          className="bg-white rounded-xl overflow-hidden"
          style={{
            boxShadow: "0 8px 40px -8px rgba(45,45,45,0.1), 0 2px 12px rgba(45,45,45,0.04)"
          }}
        >
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16/9" }}>
            <img
              src={VENUE_IMAGE}
              alt="Levona - Arava Desert"
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="venue-image"
            />
          </div>

          <div className="p-6 md:p-8">
            <p className="font-sans text-secondary text-sm tracking-wide mb-1">
              {t("venue_location_label")}
            </p>
            <h3 data-testid="venue-name" className="font-serif text-charcoal text-xl md:text-2xl mb-6">
              {t("venue_name")}
            </h3>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-cream flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} />
                </div>
                <span className="font-sans text-charcoal text-sm md:text-base">
                  {t("venue_address")}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-cream flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-gold" strokeWidth={1.5} />
                </div>
                <span className="font-sans text-charcoal text-sm md:text-base">
                  {t("venue_date_label")}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                data-testid="waze-button"
                href={WAZE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-white font-sans text-sm tracking-wide px-6 py-3.5 rounded-full hover:bg-[#b8933f] transition-all duration-300"
              >
                <Navigation className="w-4 h-4" strokeWidth={1.5} />
                {t("venue_directions")}
              </a>
              <a
                data-testid="calendar-button"
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gold text-gold font-sans text-sm tracking-wide px-6 py-3.5 rounded-full hover:bg-gold hover:text-white transition-all duration-300"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                {t("venue_add_calendar")}
              </a>
            </div>
          </div>
        </motion.div>

        {/* Shuttle */}
        <motion.div variants={fadeIn} className="mt-16 text-center">
          <Bus className="w-6 h-6 text-gold mx-auto mb-4" strokeWidth={1} />
          <h3 className="font-serif text-charcoal text-xl md:text-2xl tracking-tight mb-4">
            {t("shuttle_title")}
          </h3>
          <p className="font-sans text-secondary text-sm md:text-base leading-relaxed max-w-lg mx-auto">
            {t("shuttle_text")}
          </p>
        </motion.div>

        {/* Accommodation */}
        <motion.div variants={fadeIn} className="mt-16">
          <div className="text-center mb-8">
            <Hotel className="w-6 h-6 text-gold mx-auto mb-4" strokeWidth={1} />
            <h3 className="font-serif text-charcoal text-xl md:text-2xl tracking-tight mb-2">
              {t("accommodation_title")}
            </h3>
            <p className="font-sans text-secondary text-sm md:text-base tracking-wide">
              {t("accommodation_subtitle")}
            </p>
          </div>

          <div className="space-y-4">
            {accommodations.map((acc, i) => (
              <div
                key={i}
                data-testid={`accommodation-${i}`}
                className="bg-white rounded-lg p-5 md:p-6"
                style={{ boxShadow: "0 2px 12px rgba(45,45,45,0.04)" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-charcoal text-base md:text-lg mb-1.5">
                      {t(acc.nameKey)}
                    </h4>
                    <p className="font-sans text-secondary text-sm leading-relaxed">
                      {t(acc.descKey)}
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <a
                        href={acc.waze}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-sans text-gold text-xs md:text-sm hover:underline"
                      >
                        <Navigation className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Waze
                      </a>
                      {acc.bookUrl && (
                        <a
                          href={acc.bookUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 font-sans text-gold text-xs md:text-sm hover:underline"
                        >
                          <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {t(acc.bookKey)}
                        </a>
                      )}
                    </div>
                  </div>
                  <span className="font-sans text-gold text-xs md:text-sm font-medium whitespace-nowrap mt-1 flex-shrink-0">
                    {t(acc.distKey)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default VenueSection;
